import { createContext } from 'react';
import * as React from 'react';

import {
  SearchForFacetValuesQueryParams,
  SearchOptions,
} from '@algolia/client-search';
import algoliasearch, { SearchClient } from 'algoliasearch/lite';
import { InstantSearch, Configure } from 'react-instantsearch-dom';

export type PlatformName = 'Docs' | 'Developer' | 'Docs Preview';
export type Locales = 'en' | 'ja';
export enum Action {
  clicked = 'clicked',
  hovered = 'hovered',
  viewed = 'viewed',
  opened = 'opened',
  closed = 'closed',
  scrolled = 'scrolled',
  submitted = 'submitted',
}

const SEARCH_DEBOUNCE_TIME = 500;
const PUBLIC_ALGOLIA_APP_ID = 'U0RXNGRK45';
const PUBLIC_ALGOLIA_API_KEY = 'dd49f5b81d238d474b49645a4daed322';
export interface AlgoliaSearchState {
  query: string | null;
}

export interface LibContextProps {
  platformName: PlatformName;
  user: boolean;
  initialSearchState?: AlgoliaSearchState;
  notifyTrackingFired?: (
    object: string,
    action: Action,
    properties?: object,
  ) => void;
  onSearchStateChange?: (searchState: AlgoliaSearchState) => void;
}

export interface LibProviderValues {
  platformName: PlatformName;
  user: boolean;
  internalSearchState: AlgoliaSearchState;
  notifyTrackingFired?: (
    object: string,
    action: Action,
    properties?: object,
  ) => void;
  onSearchStateChange?: (searchState: AlgoliaSearchState) => void;
}

interface Props {
  children: React.ReactNode;
}

/**
 * Converts a given search state into a new path we can use with router.replace()
 * For example: { query: 'foo' } => /page?query=foo
 * and: { } => /page
 */
export const searchStatetoPath = (
  currentPath: string,
  searchState: AlgoliaSearchState,
): string => {
  const basePath = currentPath.includes('?')
    ? currentPath.split('?')[0]
    : currentPath;

  if (!searchState.query) {
    return basePath;
  }

  return `${basePath}?query=${searchState.query}`;
};

export const LibProvider = ({
  children,
  platformName,
  user,
  notifyTrackingFired,
  initialSearchState,
  onSearchStateChange,
}: LibContextProps & Props): JSX.Element => {
  initialSearchState = initialSearchState ?? { query: '' };

  // algolia integration
  const algoliaClient = algoliasearch(
    PUBLIC_ALGOLIA_APP_ID,
    PUBLIC_ALGOLIA_API_KEY,
  );
  const algoliaSearchClient = {
    ...algoliaClient,
    search(
      requests: ReadonlyArray<{
        readonly indexName: string;
        readonly params: SearchForFacetValuesQueryParams & SearchOptions;
      }>,
    ): ReturnType<SearchClient['search']> {
      if (
        requests.every(({ params }) => {
          return !params.query;
        })
      ) {
        // no query, skipping search
        return;
      }

      return algoliaClient.search(requests);
    },
  };

  const debouncedSetStateRef = React.useRef<NodeJS.Timeout>();
  const [internalSearchState, setInternalSearchState] =
    React.useState(initialSearchState);

  const onInternalSearchStateChange = (
    nextSearchState: AlgoliaSearchState,
  ): void => {
    if (debouncedSetStateRef.current) {
      clearTimeout(debouncedSetStateRef.current);
    }

    const { query } = nextSearchState;

    // if we have a query now and didn't had one before
    if (query && !internalSearchState.query) {
      notifyTrackingFired &&
        notifyTrackingFired('search-results', Action.opened);
    }

    // setting a debounce so we don't update too many time the platform
    debouncedSetStateRef.current = setTimeout(() => {
      onSearchStateChange && onSearchStateChange({ query });
    }, SEARCH_DEBOUNCE_TIME);

    setInternalSearchState({ query });
  };

  return (
    <LibContext.Provider
      value={{
        platformName,
        user,
        notifyTrackingFired,
        internalSearchState,
      }}
    >
      <InstantSearch
        searchClient={algoliaSearchClient}
        indexName="documentation"
        searchState={internalSearchState}
        onSearchStateChange={onInternalSearchStateChange}
      >
        <Configure removeWordsIfNoResults="allOptional" />
        {children}
      </InstantSearch>
    </LibContext.Provider>
  );
};

export const LibContext = createContext<LibProviderValues | null>(
  null,
) as React.Context<LibProviderValues>;
